/* HR — Employees page
 *  endpoints used:
 *    GET    /api/employees?page=&page_size=&department_id=&status=&include_deleted=&q=
 *    GET    /api/employees/:id
 *    POST   /api/employees
 *    PATCH  /api/employees/:id
 *    DELETE /api/employees/:id           (soft delete)
 *    POST   /api/employees/:id/restore   (un-soft-delete)
 *    GET    /api/departments?active_only=true   (filter + form picker)
 */

/* ─────────────────────────  Helpers  ───────────────────────── */

/* Expressive (GX) 樣式 helper — Bento 下 uiStyle 原樣回傳傳入的字面值（輸出不變）；
 * 只在 render 內呼叫（plan-expressive-inner-pages.md §2(d)）。 */
function EMP_gxEyebrow(bento) {   // 小型大寫 eyebrow → 無大寫字距、onV 700
  return uiStyle(bento, { letterSpacing: 0, textTransform: 'none', fontWeight: GxT.weight.label, color: GxT.color.onV });
}
function EMP_gxTile(bento) {      // KPI 磚 → 無框卡片 16
  return uiStyle(bento, { border: 'none', borderRadius: GxT.radius.card, background: GxT.color.card });
}
function EMP_gxKpiNum(bento) {    // KPI 大數字 → 800、tabular-nums
  return uiStyle(bento, {
    fontSize: 'clamp(28px, 3.2vw, 44px)', fontWeight: GxT.weight.title,
    fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.5px',
  });
}
function EMP_gxInner(bento) {     // 卡內子區塊 → 圓角 12、無框
  return uiStyle(bento, { borderRadius: GxT.radius.inner, border: 'none' });
}
function EMP_gxField(bento) {     // 本頁自刻的輸入框 → outlined（與 primitives inputStyle GX 版一致）
  return uiStyle(bento, {
    minHeight: 36, borderRadius: GxT.radius.input, border: `1px solid ${GxT.color.ol}`,
    background: GxT.color.card, fontFamily: GxT.font,
  });
}

/* Expressive 頁面專屬 CSS（只在新版外殼注入；每條選擇器以 html[data-ui-shell="expressive"] 開頭）：
 *  · .hr-emp-lede-stats（儀表板 lede）→ 無框 tonal 磚，蓋掉 index.html ≤767 的 !important 分隔線
 *  · .gx-p-emp-kpis（員工頁 KPI 列，GX 才掛的 class）→ ≤767 時 index.html 把它改成單欄且 gap 歸 0；GX 改兩欄、留 8px 間距 */
if (window.GxCore) window.GxCore.injectCss('gx-css-p-employees', String.raw`
html[data-ui-shell="expressive"] .gx-page .hr-emp-lede-stats{border-bottom:0!important;padding-bottom:0!important;gap:12px!important}
html[data-ui-shell="expressive"] .gx-page .hr-emp-lede-stats > div{border:0!important;border-radius:16px;background:var(--gx-card);padding:18px 20px!important;min-width:0}
html[data-ui-shell="expressive"] .gx-page .gx-p-emp-kpis{gap:12px!important}
@media (max-width:767px){
html[data-ui-shell="expressive"] .gx-page .hr-emp-lede-stats{gap:8px!important}
html[data-ui-shell="expressive"] .gx-page .hr-emp-lede-stats > div{padding:16px!important}
html[data-ui-shell="expressive"] .gx-page .gx-p-emp-kpis{gap:8px!important;grid-template-columns:repeat(2,minmax(0,1fr))!important}
}
`);

function avatarColor(seed) {
  const palette = ['#D6CEB9', '#C3D6CC', '#E4D5C6', '#D4C8DB', '#DBCEC8', '#C8D0DB', '#D8C5C0'];
  let h = 0;
  for (let i = 0; i < (seed || '').length; i++) h = ((h << 5) - h) + seed.charCodeAt(i);
  return palette[Math.abs(h) % palette.length];
}

function Avatar({ name, size = 28 }) {
  const initials = (name || '?').slice(0, 1);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: size, height: size, borderRadius: '50%',
      background: avatarColor(name), color: HR.ink,
      fontSize: Math.max(9, size * 0.36), fontWeight: 600, flexShrink: 0,
    }}>{initials}</span>
  );
}

function StatusPill({ code, enums }) {
  const opt = enums.byCode[code];
  if (!opt) return <span style={{ color: HR.inkFaint, fontSize: FontSizes.xxs }}>—</span>;
  const color = opt.color || HR.inkSoft;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: FontSizes.xxs, color: HR.inkSoft,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
      {opt.name}
    </span>
  );
}

/* ─────────────────────────  Filter bar  ─────────────────────── */

function FilterBar({ filters, setFilters, departments, statuses, onReset }) {
  const [searchTerm, setSearchTerm] = React.useState(filters.q || '');

  // Debounce search → filters.q
  React.useEffect(() => {
    const t = setTimeout(() => {
      if (searchTerm !== filters.q) setFilters(f => ({ ...f, q: searchTerm, page: 1 }));
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  React.useEffect(() => { setSearchTerm(filters.q || ''); }, [filters.q]);

  return (
    <div style={uiStyle({
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      padding: '14px 24px', borderBottom: `1px solid ${HR.rule}`, background: HR.surfaceAlt,
    }, { padding: '18px 20px 14px', borderBottom: `1px solid ${GxT.color.s3}`, background: 'transparent' })}>
      <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 360 }}>
        <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: HR.inkFaint, pointerEvents: 'none' }}>
          <Icon name="search" size={13} />
        </span>
        <input
          value={searchTerm}
          placeholder="搜尋姓名 / 員工編號 / Email…"
          onChange={(e) => setSearchTerm(e.target.value)}
          style={EMP_gxField({
            width: '100%', padding: '8px 11px 8px 32px', fontSize: FontSizes.xs,
            border: `1px solid ${HR.rule}`, borderRadius: 4, background: HR.surface,
            outline: 'none', fontFamily: HR.sans, color: HR.ink,
          })}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={EMP_gxEyebrow({ fontSize: FontSizes.trading_lg, letterSpacing: '0.08em', textTransform: 'uppercase', color: HR.inkFaint })}>部門</span>
        <select
          value={filters.department_id || ''}
          onChange={(e) => setFilters(f => ({ ...f, department_id: e.target.value || null, page: 1 }))}
          style={{ ...inputBoxStyle(), width: 160 }}
        >
          <option value="">全部</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={EMP_gxEyebrow({ fontSize: FontSizes.trading_lg, letterSpacing: '0.08em', textTransform: 'uppercase', color: HR.inkFaint })}>狀態</span>
        <select
          value={filters.status || ''}
          onChange={(e) => setFilters(f => ({ ...f, status: e.target.value || null, page: 1 }))}
          style={{ ...inputBoxStyle(), width: 130 }}
        >
          <option value="">全部</option>
          {statuses.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
        </select>
      </div>
      <Checkbox
        checked={filters.include_deleted}
        onChange={(v) => setFilters(f => ({ ...f, include_deleted: v, page: 1 }))}
        label="含已刪除"
      />
      <div style={{ flex: 1 }} />
      <Button size="sm" variant="ghost" onClick={onReset}>清除篩選</Button>
    </div>
  );
}

function inputBoxStyle() {
  return EMP_gxField({
    padding: '7px 11px', fontSize: FontSizes.xs, fontFamily: HR.sans,
    border: `1px solid ${HR.rule}`, borderRadius: 4,
    background: HR.surface, color: HR.ink, outline: 'none',
  });
}

/* Pagination moved to hr-shell.jsx (now generic, shared across modules) */

/* ─────────────────────────  Form sections  ─────────────────── */

function Section({ title, italic, children }) {
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={uiStyle({
        display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 14,
        paddingBottom: 6, borderBottom: `1px solid ${HR.ruleSoft}`,
      }, { paddingBottom: 0, borderBottom: 'none' })}>
        <span style={uiStyle(
          { fontFamily: HR.serif, fontSize: FontSizes.md, fontWeight: 500 },
          { fontSize: GxT.size.h3, fontWeight: GxT.weight.title },
        )}>{title}</span>
        {italic && <span style={uiStyle(
          { fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontFamily: HR.mono, letterSpacing: '0.04em' },
          { fontFamily: GxT.font, letterSpacing: 0, fontWeight: GxT.weight.medium },
        )}>{italic}</span>}
      </div>
      <div className="hr-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {children}
      </div>
    </div>
  );
}

function EnumSelect({ enumType, value, onChange, allowEmpty = true }) {
  const e = useEnum(enumType);
  return (
    <Select
      value={value}
      onChange={onChange}
      options={e.options.map(o => ({ value: o.code, label: o.name }))}
      allowEmpty={allowEmpty}
    />
  );
}

function DepartmentSelect({ value, onChange, departments }) {
  return (
    <Select
      value={value || ''}
      onChange={(v) => onChange(v ? parseInt(v, 10) : null)}
      options={departments.map(d => ({ value: d.id, label: d.name }))}
    />
  );
}

/* ─────────────────────────  Employee form (modal)  ─────────── */

// weekday 採 ISO 8601: 1=週一 ... 7=週日。WEEKDAY_LABELS index 對應 weekday 值（0 留空）。
const WEEKDAY_LABELS = [null, '一', '二', '三', '四', '五', '六', '日'];
const WEEKDAY_VALUES = [1, 2, 3, 4, 5, 6, 7];

// 預設：週一~五 09:00-17:30；週六/日休息（active=false）
function defaultWorkSchedule() {
  return WEEKDAY_VALUES.map(wd => ({
    weekday: wd,
    active: wd >= 1 && wd <= 5,
    start_time: '09:00',
    end_time: '17:30',
  }));
}

// 從 API 回傳的 work_schedule 陣列填回 7 列 state（補休息日）
function initWorkScheduleFromAPI(arr) {
  const base = defaultWorkSchedule().map(d => ({ ...d, active: false }));
  const idxOfWd = (wd) => WEEKDAY_VALUES.indexOf(wd);  // wd 1~7 → array index 0~6
  for (const r of arr || []) {
    const i = idxOfWd(r.weekday);
    if (i < 0) continue;
    base[i] = {
      weekday: r.weekday,
      active: !!(r.start_time && r.end_time),
      start_time: r.start_time || '09:00',
      end_time: r.end_time || '18:00',
    };
  }
  return base;
}

// 計算平均工時（即時顯示用）
function avgHoursPerDay(rows) {
  const totals = [];
  for (const r of rows) {
    if (!r.active || !r.start_time || !r.end_time) continue;
    try {
      const [sh, sm] = r.start_time.split(':').map(Number);
      const [eh, em] = r.end_time.split(':').map(Number);
      const mins = (eh * 60 + em) - (sh * 60 + sm) - 30;
      if (mins > 0) totals.push(mins / 60);
    } catch { /* skip */ }
  }
  if (!totals.length) return 0;
  return Math.round((totals.reduce((a, b) => a + b, 0) / totals.length) * 10) / 10;
}


function EmployeeForm({ initial, departments, onCancel, onSaved }) {
  const toast = useToast();
  const isEdit = !!(initial && initial.id);

  const [f, setF] = React.useState(() => ({
    emp_code: initial?.emp_code || '',
    user_id: initial?.user_id || null,
    name: initial?.name || '',
    name_en: initial?.name_en || '',
    email: initial?.email || '',
    phone: initial?.phone || '',
    gender: initial?.gender || null,
    birth_date: initial?.birth_date || '',
    id_number: initial?.id_number || '',
    department_id: initial?.department_id || null,
    position: initial?.position || '',
    employment_type: initial?.employment_type || null,
    hire_date: initial?.hire_date || '',
    resign_date: initial?.resign_date || '',
    // 離職類型（enum_options.separation_type：voluntary 自願離職 / layoff 資遣）。
    // 只有填了離職日才有意義；資遣（layoff）時最後一期薪資單會計算資遣費與年終保障獎金。
    separation_type: initial?.separation_type || null,
    status: initial?.status || 'active',
    manager_id: initial?.manager_id || null,
    manager_label: initial?.manager_name || '',
    bank_code: initial?.bank_code || '',
    bank_account: initial?.bank_account || '',
    emergency_contact: initial?.emergency_contact || '',
    emergency_phone: initial?.emergency_phone || '',
    address: initial?.address || '',
    avatar_url: initial?.avatar_url || '',
    remark: initial?.remark || '',
    dependents: initial?.dependents ?? 0,
    custom_insurance_cap: initial?.custom_insurance_cap != null ? String(initial.custom_insurance_cap) : '',
    // 合約月薪：
    //  - 新增模式：後端會自動建第一筆 employee_salary_history（effective_from = hire_date 月初）
    //  - 編輯模式：載入目前生效薪資；若修改 → POST /employees/{id}/salaries 建新一筆調薪
    //    （effective_from = 本月 1 號）
    total_salary: initial?.total_salary != null ? String(initial.total_salary) : '',
    // 工作時間（7 筆 weekday 0~6）。新增模式預設週一~五 9~18 + 60 分午休；
    // 編輯模式從 initial.work_schedule 載入。
    work_schedule: initial?.work_schedule
      ? initWorkScheduleFromAPI(initial.work_schedule)
      : defaultWorkSchedule(),
    // 登入帳號設定：email = username（POST /auth/register → 取 user_id → 寫入 employees.user_id）。
    // 編輯模式下，password 留空 = 不變動；填了會打 PATCH /admin/users/:user_id/password。
    password: '',
    password2: '',
  }));
  const [busy, setBusy] = React.useState(false);
  const [errors, setErrors] = React.useState({});

  // RBAC role assignment (create mode only). Default to 'employee' so every
  // new account at minimum has basic login + own-data access. Admin / hr /
  // finance / manager / trade_editor are opt-in checkboxes.
  const [availableRoles, setAvailableRoles] = React.useState([]);
  const [selectedRoles, setSelectedRoles] = React.useState(['employee']);
  React.useEffect(() => {
    if (isEdit) return;  // edit mode uses the dedicated /roles UI for role mgmt
    api('GET', '/roles?active_only=true')
      .then(list => setAvailableRoles(Array.isArray(list) ? list : []))
      .catch(() => setAvailableRoles([]));
  }, [isEdit]);

  const toggleRole = (code) => {
    setSelectedRoles(prev => prev.includes(code)
      ? prev.filter(c => c !== code)
      : [...prev, code]);
  };

  const set = (k) => (v) => setF(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    const errs = {};
    if (!f.name.trim()) errs.name = '姓名必填';
    if (!f.hire_date) errs.hire_date = '到職日必填';
    // 後端：separation_type 有值時 resign_date 必填（400）；這裡提早擋
    if (f.separation_type && !f.resign_date) errs.separation_type = '已選離職類型時，離職日必填';

    // Email / password rules
    if (!isEdit) {
      // Create flow: email required (email = username)
      if (!f.email.trim()) errs.email = 'Email 必填（將作為登入帳號）';
    } else {
      // Edit flow: password optional, but if filled must match + min length
      if (f.password) {
        if (f.password.length < 8) errs.password = '新密碼至少 8 字元';
        if (f.password !== f.password2) errs.password2 = '兩次密碼不一致';
      }
    }

    // 合約月薪：新增模式必填；編輯模式選填（沒改 = 不送調薪）
    const sal = parseFloat(f.total_salary);
    if (!isEdit) {
      if (!f.total_salary || !(sal > 0)) {
        errs.total_salary = '合約月薪必填（建立員工時會自動寫入第一筆薪資歷史）';
      }
    } else if (f.total_salary !== '' && (isNaN(sal) || sal <= 0)) {
      errs.total_salary = '月薪必須大於 0';
    }

    // work_schedule 檢查：active=true 才送（休息日略過）；至少要有 1 個工作日
    const sched = (f.work_schedule || [])
      .filter(r => r.active && r.start_time && r.end_time)
      .map(r => ({
        weekday: r.weekday,
        start_time: r.start_time,
        end_time: r.end_time,
      }));
    if (!sched.length) {
      errs.work_schedule = '至少需設定 1 個工作日（全部休息無法計薪）';
    } else {
      for (const r of sched) {
        const [sh, sm] = r.start_time.split(':').map(Number);
        const [eh, em] = r.end_time.split(':').map(Number);
        const work_min = (eh * 60 + em) - (sh * 60 + sm) - 30;
        if (work_min <= 0) {
          errs.work_schedule = `星期${WEEKDAY_LABELS[r.weekday]}扣除午休後工時須 > 0`;
          break;
        }
      }
    }

    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({}); setBusy(true);

    // strip empty strings → null; strip non-API/transient fields
    // total_salary 不在 EmployeeUpdate schema：
    //   新增模式 → 包進 POST /employees payload（後端建第一筆 salary_history）
    //   編輯模式 → 若改了 → 編輯完後另外 POST /employees/{id}/salaries 建調薪
    const payload = {};
    const SKIP = new Set(['manager_label', 'password', 'password2', 'work_schedule']);
    if (isEdit) SKIP.add('total_salary');
    Object.keys(f).forEach(k => {
      if (SKIP.has(k)) return;
      let v = f[k];
      if (k === 'total_salary' && v !== '' && v !== null) v = parseFloat(v);
      if (k === 'custom_insurance_cap' && v !== '' && v !== null) v = parseInt(v, 10);
      payload[k] = v === '' ? null : v;
    });
    payload.work_schedule = sched;

    // 編輯模式：判斷 salary 是否變動 → 觸發調薪
    const newSalary = f.total_salary !== '' ? parseFloat(f.total_salary) : null;
    const oldSalary = initial?.total_salary != null ? parseFloat(initial.total_salary) : null;
    const salaryChanged = isEdit && newSalary !== null
      && newSalary > 0 && newSalary !== oldSalary;

    try {
      if (isEdit) {
        await api('PATCH', `/employees/${initial.id}`, payload);

        // 若 salary 變動：patch 當月調薪紀錄
        let salaryAdjusted = false;
        if (salaryChanged) {
          try {
            await api('PATCH', `/employees/${initial.id}/salaries/current`, {
              total_salary: newSalary,
              note: '從員工編輯頁調薪',
            });
            salaryAdjusted = true;
          } catch (e) {
            toast('調薪失敗：' + (e.detail || ''), 'error');
          }
        }

        // 密碼重設（選填）— 必須先有 user_id 綁定才能 reset
        if (f.password) {
          const uid = initial?.user_id;
          if (!uid) {
            toast('員工資料已更新，但此員工尚未綁定登入帳號（user_id 為空），無法重設密碼', 'info');
          } else {
            try {
              await api('PATCH', `/admin/users/${uid}/password`, { password: f.password });
              toast('員工資料已更新（含密碼重設' + (salaryAdjusted ? '、調薪' : '') + '）', 'ok');
            } catch (e) {
              toast('員工資料已更新，但密碼重設失敗：' + (e.detail || ''), 'error');
            }
          }
        } else {
          toast('員工資料已更新' + (salaryAdjusted ? `（含調薪 ${fmtMoney(newSalary)}）` : ''), 'ok');
        }
      } else {
        // 2-step create: 先 register user（email = username）→ 拿 user_id → 建 employee 並綁定
        let createdUserId = null;
        let emailSent = false;
        let tempPassword = null;
        const username = f.email.trim();
        try {
          const reg = await api('POST', '/auth/register', {
            username,
          });
          createdUserId = reg?.user_id || null;
          emailSent = !!reg?.email_sent;
          tempPassword = reg?.temp_password || null;
        } catch (e) {
          if (e.status === 400 && /已存在/.test(e.detail || '')) {
            // username 已被用 — 用 admin/users 反查既有帳號，但不再靜默綁定：
            //   ① 非員工帳號（non_staff / analyst）→ 直接擋下（後端也會 400，這裡提早給明確錯誤）
            //   ② 其他帳號 → 明確 confirm 後才綁定，取消則中止建立
            try {
              // /admin/users 現在回 PaginatedResponse: { items, total, page, page_size }
              const resp = await api('GET', `/admin/users?username=${encodeURIComponent(username)}`);
              const items = resp?.items || [];
              if (items.length === 1) {
                const existing = items[0];
                const existingRoles = existing.roles || [];
                if (existingRoles.includes('non_staff') || existingRoles.includes('analyst')) {
                  toast(`該帳號為非員工帳號（non_staff/analyst），不可綁定員工檔`, 'error');
                  return; // 中止建立（finally 會解除 busy）
                }
                const ok = window.confirm(
                  `帳號 ${username} 已存在（角色：${existingRoles.join(', ') || '無'}）。\n` +
                  `確定要把這個既有帳號綁定到這筆新員工嗎？`
                );
                if (!ok) {
                  toast('已取消：未綁定既有帳號，員工未建立', 'info');
                  return; // 中止建立
                }
                createdUserId = existing.id;
                toast(`帳號 ${username} 已存在，將綁定既有 user (#${createdUserId})`, 'info');
              } else {
                toast(`帳號 ${username} 已存在但反查失敗；員工會以無登入帳號狀態建立`, 'error');
              }
            } catch {
              toast(`帳號 ${username} 已存在；員工會以無登入帳號狀態建立`, 'error');
            }
          } else {
            throw e;
          }
        }
        payload.user_id = createdUserId;
        await api('POST', '/employees', payload);

        // Grant RBAC roles to the new user (best-effort — if a role fails,
        // toast a warning but don't roll back the employee). Admin can fix
        // role assignment afterwards via /roles UI.
        let grantedCount = 0;
        const grantErrors = [];
        if (createdUserId && selectedRoles.length > 0) {
          for (const code of selectedRoles) {
            try {
              await api('POST', `/users/${createdUserId}/roles`, { role_code: code });
              grantedCount += 1;
            } catch (e) {
              // 409 = already has this role (e.g., 'employee' auto-granted by legacy
              // role migration) → count as success, not an error.
              if (e.status === 409) {
                grantedCount += 1;
              } else {
                grantErrors.push(`${code}: ${e.detail || e.message || 'failed'}`);
              }
            }
          }
        }

        if (grantErrors.length > 0) {
          toast(`員工已建立但部分角色指派失敗 (${grantErrors.join('; ')})。請至「角色與權限」手動補。`, 'warn');
        } else if (createdUserId) {
          if (tempPassword) {
            if (emailSent) {
              toast(`員工 & 登入帳號已建立，初始密碼已成功發送至 ${username}`, 'ok');
            } else {
              toast(`員工 & 登入帳號已建立，但 Email 發送失敗。初始密碼為：${tempPassword} (請手動告知員工)`, 'warn');
            }
          } else {
            toast(`員工 & 登入帳號已建立並綁定 (user #${createdUserId}, ${grantedCount} 個角色)`, 'ok');
          }
        } else {
          toast('員工已建立（未綁定登入帳號，因此未指派角色）', 'ok');
        }
      }
      onSaved();
    } catch (e) {
      toast(e.detail || '儲存失敗', 'error');
    } finally { setBusy(false); }
  };

  return (
    <Modal
      open
      onClose={busy ? null : onCancel}
      title={isEdit ? `編輯員工 — ${initial.name}` : '新增員工'}
      subtitle={isEdit ? `Edit Employee #${initial.id}` : 'New Employee'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={busy}>
            {busy ? '儲存中…' : (isEdit ? '儲存變更' : '建立員工')}
          </Button>
        </>
      }
    >
      <Section title="基本資料" italic="Profile">
        <Field label="員工編號" hint="UNIQUE，可留空由系統指派">
          <TextInput value={f.emp_code} onChange={set('emp_code')} placeholder="e.g. E001" mono />
        </Field>
        <Field label="姓名" required error={errors.name}>
          <TextInput value={f.name} onChange={set('name')} placeholder="張小明" />
        </Field>
        <Field label="英文姓名">
          <TextInput value={f.name_en} onChange={set('name_en')} placeholder="Xiaoming Zhang" />
        </Field>
        <Field label="性別">
          <EnumSelect enumType="gender" value={f.gender} onChange={set('gender')} />
        </Field>
        <Field
          label="Email · 登入帳號"
          required={!isEdit}
          error={errors.email}
          hint="此 email 將作為系統登入帳號（username）"
        >
          <TextInput
            type="email"
            value={f.email}
            onChange={set('email')}
            placeholder="user@gsinvest.com.tw"
            disabled={isEdit && !!initial?.email}
          />
        </Field>
        <Field label="電話">
          <TextInput value={f.phone} onChange={set('phone')} placeholder="0912-345-678" mono />
        </Field>
        <Field label="生日">
          <TextInput type="date" value={f.birth_date} onChange={set('birth_date')} />
        </Field>
        <Field label="身分證字號" hint="敏感資料，僅 HR 可見">
          <TextInput value={f.id_number} onChange={set('id_number')} placeholder="A123456789" mono />
        </Field>
      </Section>

      <Section title="登入帳號" italic="Account Credentials">
        {/* Read-only bind status (edit mode only) — hides raw user_id from end users */}
        {isEdit && (
          <div style={EMP_gxInner({
            gridColumn: 'span 2', marginBottom: 4,
            padding: '10px 14px', borderRadius: 4,
            background: f.user_id ? HR.surfaceAlt : '#FAEFD8',
            border: `1px solid ${f.user_id ? HR.rule : GxCore.alpha(HR.warn, '33')}`,
            display: 'flex', alignItems: 'center', gap: 10, fontSize: FontSizes.trading_md,
          })}>
            {f.user_id ? (
              <>
                <span style={{
                  width: 7, height: 7, borderRadius: '50%', background: HR.accent,
                }} />
                <span style={{ color: HR.inkSoft }}>已綁定登入帳號</span>
                <strong style={{ fontFamily: HR.mono, color: HR.ink }}>
                  {initial?.username || f.email || '—'}
                </strong>
                <span style={{
                  fontFamily: HR.mono, fontSize: FontSizes.trading_md, color: HR.inkFaint,
                  padding: '1px 6px', background: HR.ruleSoft, borderRadius: 2,
                  marginLeft: 'auto',
                }}>
                  user #{f.user_id}
                </span>
              </>
            ) : (
              <>
                <span style={{ color: HR.warn, fontSize: FontSizes.sm }}>⚠</span>
                <span style={{ color: HR.ink }}>
                  尚未綁定登入帳號 — 此員工無法登入系統。
                </span>
                <span style={{
                  fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginLeft: 'auto',
                }}>
                  填下方密碼欄即可建立 / 綁定
                </span>
              </>
            )}
          </div>
        )}
        {isEdit ? (
          <>
            <Field
              label="重設密碼"
              error={errors.password}
              hint="留空 = 不變更；填寫即重設密碼"
            >
              <TextInput
                type="password"
                value={f.password}
                onChange={set('password')}
                placeholder="（保持不變）"
                autoComplete="new-password"
              />
            </Field>
            <Field
              label="再次輸入密碼"
              required={!!f.password}
              error={errors.password2}
            >
              <TextInput
                type="password"
                value={f.password2}
                onChange={set('password2')}
                placeholder="（保持不變）"
                autoComplete="new-password"
                disabled={!f.password}
              />
            </Field>
          </>
        ) : null}
        <div style={EMP_gxInner({
          gridColumn: 'span 2',
          padding: '10px 14px', background: HR.accentSoft, borderRadius: 4,
          fontSize: FontSizes.trading_xl, color: HR.accent, lineHeight: 1.5,
          display: 'flex', alignItems: 'flex-start', gap: 8,
        })}>
          <Icon name="more" size={12} stroke={2} />
          {isEdit ? (
            <span>
              員工會以 <strong style={{ fontFamily: HR.mono }}>{f.email || '（請先填 email）'}</strong>
              {' '}登入主站 → 自動拉到對應的個人資料。
            </span>
          ) : (
            <span>
              系統將會為 <strong style={{ fontFamily: HR.mono }}>{f.email || '（請先填 email）'}</strong>
              {' '}自動生成隨機密碼並發信通知，提醒員工登入後變更密碼。
            </span>
          )}
        </div>
      </Section>

      {/* RBAC 角色 — create-mode only. Edit-mode users管 roles 走 /roles 頁面，
       * 因為那邊有完整 grant/revoke + audit + 多人批次。 */}
      {!isEdit && (
        <Section title="RBAC 角色" italic="Roles & Permissions">
          <div style={{ gridColumn: 'span 2' }}>
            <div style={{ fontSize: FontSizes.trading_xl, color: HR.inkSoft, marginBottom: 10, lineHeight: 1.5 }}>
              預設每位員工至少要勾「一般員工」 — 沒有任何 role 等於登入後什麼都不能做。
              其他依職務勾選；不確定就先不勾，之後可至「角色與權限」頁面再補。
            </div>
            {availableRoles.length === 0 ? (
              <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, fontStyle: 'italic' }}>載入角色清單中…</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                {availableRoles.map(r => {
                  const checked = selectedRoles.includes(r.code);
                  const isAdminRole = r.code === 'admin';
                  return (
                    <label
                      key={r.code}
                      style={uiStyle({
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 12px', borderRadius: 4, cursor: 'pointer',
                        background: checked ? (isAdminRole ? '#FAEFD8' : HR.accentSoft) : HR.surfaceAlt,
                        border: `1px solid ${checked ? (isAdminRole ? HR.warn : HR.accent) : HR.rule}`,
                        transition: 'background 120ms',
                      }, {
                        // 選取狀態仍以外框色表示（不只靠底色），未選為無框 tonal；圓角 12
                        borderRadius: GxT.radius.inner, minHeight: 44,
                        border: `1px solid ${checked ? (isAdminRole ? HR.warn : HR.accent) : 'transparent'}`,
                      })}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRole(r.code)}
                        style={{ width: 14, height: 14, cursor: 'pointer' }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: FontSizes.xs, fontWeight: 500, color: HR.ink }}>
                          {r.name}
                          {isAdminRole && checked && (
                            <span style={{
                              marginLeft: 6, fontSize: FontSizes.trading_sm, padding: '1px 6px',
                              background: HR.warn, color: '#fff', borderRadius: 2,
                              letterSpacing: '0.05em',
                            }}>HIGH PRIVILEGE</span>
                          )}
                        </div>
                        <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontFamily: HR.mono, marginTop: 2 }}>
                          {r.code}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            {selectedRoles.includes('admin') && (
              <div style={EMP_gxInner({
                marginTop: 10, padding: '10px 14px', background: '#FAEFD8',
                border: `1px solid ${GxCore.alpha(HR.warn, '33')}`, borderRadius: 4,
                fontSize: FontSizes.trading_xl, color: HR.warn, lineHeight: 1.5,
              })}>
                ⚠ <strong>admin</strong> 擁有全系統 24 個 capability（含修改 RBAC、查所有薪資、停用任何帳號）。建議只給公司 1-2 位指定總管。
              </div>
            )}
          </div>
        </Section>
      )}

      <Section title="任職資料" italic="Employment">
        <Field label="部門">
          <DepartmentSelect value={f.department_id} onChange={set('department_id')} departments={departments} />
        </Field>
        <Field label="職稱">
          <TextInput value={f.position} onChange={set('position')} placeholder="工程師" />
        </Field>
        <Field label="僱用類型">
          <EnumSelect enumType="employment_type" value={f.employment_type} onChange={set('employment_type')} />
        </Field>
        <Field label="在職狀態">
          <EnumSelect enumType="employee_status" value={f.status} onChange={set('status')} allowEmpty={false} />
        </Field>
        <Field label="到職日" required error={errors.hire_date}>
          <TextInput type="date" value={f.hire_date} onChange={set('hire_date')} />
        </Field>
        <Field label="離職日">
          <TextInput type="date" value={f.resign_date}
            onChange={(v) => setF(p => ({ ...p, resign_date: v, separation_type: v ? p.separation_type : null }))} />
        </Field>
        <Field label="離職類型" hint="需先填離職日；選「資遣」時，最後一期薪資單會自動計算資遣費與年終保障獎金" error={errors.separation_type}>
          <EnumSelect enumType="separation_type" value={f.separation_type} onChange={set('separation_type')} />
        </Field>
        <Field label="直屬主管" span={2}>
          <EmployeePicker
            value={f.manager_id}
            valueLabel={f.manager_label}
            onChange={(id, label) => setF(p => ({ ...p, manager_id: id, manager_label: label || '' }))}
          />
        </Field>
      </Section>

      <Section title="薪轉與緊急聯絡" italic="Banking & Emergency">
        <Field label="銀行代碼">
          <TextInput value={f.bank_code} onChange={set('bank_code')} placeholder="012" mono />
        </Field>
        <Field label="銀行帳號">
          <TextInput value={f.bank_account} onChange={set('bank_account')} placeholder="1234567890" mono />
        </Field>
        <Field label="健保眷屬人數" hint="影響健保自付額計算；0=無眷屬">
          <TextInput
            type="number"
            value={f.dependents}
            onChange={(v) => set('dependents')(parseInt(v, 10) || 0)}
            placeholder="0"
            mono
          />
        </Field>
        <Field label="自訂薪資與投保上限" hint="未填則預設採用系統 45,800 上限；影響健保基數上限與實存上限">
          <TextInput
            type="number"
            value={f.custom_insurance_cap}
            onChange={set('custom_insurance_cap')}
            placeholder="預設 45,800"
            mono
          />
        </Field>
        <Field
          label={isEdit ? "合約月薪" : "合約月薪 (total_salary)"}
          required={!isEdit}
          hint={isEdit
            ? "顯示目前生效中的月薪；修改後送出會自動建本月調薪紀錄（effective_from = 本月 1 號）"
            : "會自動寫入第一筆薪資歷史（effective_from = 到職日月初）"}
          error={errors.total_salary}
        >
          <TextInput
            type="number"
            value={f.total_salary}
            onChange={set('total_salary')}
            placeholder="45000"
            mono
          />
        </Field>
        <Field label="緊急聯絡人">
          <TextInput value={f.emergency_contact} onChange={set('emergency_contact')} />
        </Field>
        <Field label="緊急聯絡電話">
          <TextInput value={f.emergency_phone} onChange={set('emergency_phone')} mono />
        </Field>
      </Section>

      <Section title="工作時間" italic="Working Hours">
        <div className="hr-schedule-outer" style={{ gridColumn: 'span 2' }}>
          {errors.work_schedule && (
            <div style={{ marginBottom: 10, padding: 8, background: '#fee', color: HR.down, fontSize: FontSizes.xxs, borderRadius: 4 }}>
              {errors.work_schedule}
            </div>
          )}
          <div className="hr-schedule-row" style={EMP_gxEyebrow({
            display: 'grid',
            gridTemplateColumns: '60px 80px 1fr 1fr 1fr',
            gap: 8, alignItems: 'center',
            fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.1em',
            textTransform: 'uppercase', paddingBottom: 8, borderBottom: `1px solid ${HR.ruleSoft}`,
          })}>
            <div>星期</div>
            <div>上班</div>
            <div>上班時間</div>
            <div>下班時間</div>
            <div>工時（時）</div>
          </div>
          {(f.work_schedule || []).map((row, idx) => {
            const updateRow = (k, v) => {
              setF(prev => {
                const next = [...prev.work_schedule];
                next[idx] = { ...next[idx], [k]: v };
                return { ...prev, work_schedule: next };
              });
            };
            // 該日扣除午休後的工時
            let dailyHrs = 0;
            if (row.active && row.start_time && row.end_time) {
              try {
                const [sh, sm] = row.start_time.split(':').map(Number);
                const [eh, em] = row.end_time.split(':').map(Number);
                const m = (eh * 60 + em) - (sh * 60 + sm) - 30;
                if (m > 0) dailyHrs = Math.round(m / 60 * 100) / 100;
              } catch { /* skip */ }
            }
            return (
              <div key={row.weekday} className="hr-schedule-row" style={{
                display: 'grid',
                gridTemplateColumns: '60px 80px 1fr 1fr 1fr',
                gap: 8, alignItems: 'center', padding: '8px 0',
                borderBottom: `1px solid ${HR.ruleSoft}`,
                opacity: row.active ? 1 : 0.5,
              }}>
                <div style={{ fontFamily: HR.serif, fontSize: FontSizes.sm }}>星期{WEEKDAY_LABELS[row.weekday]}</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: FontSizes.xxs }}>
                  <input
                    type="checkbox"
                    checked={row.active}
                    onChange={(e) => updateRow('active', e.target.checked)}
                  />
                  {row.active ? '上班' : '休息'}
                </label>
                <input type="time" value={row.start_time} disabled={!row.active}
                  onChange={(e) => updateRow('start_time', e.target.value)}
                  style={inputStyle({ fontFamily: HR.mono, fontSize: FontSizes.xs })} />
                <input type="time" value={row.end_time} disabled={!row.active}
                  onChange={(e) => updateRow('end_time', e.target.value)}
                  style={inputStyle({ fontFamily: HR.mono, fontSize: FontSizes.xs })} />
                <div style={{ fontFamily: HR.mono, textAlign: 'right', fontSize: FontSizes.xs, color: row.active ? HR.ink : HR.inkFaint }}>
                  {row.active ? `${dailyHrs} h` : '—'}
                </div>
              </div>
            );
          })}
          <div style={EMP_gxInner({
            marginTop: 10, padding: '10px 12px', background: HR.surfaceAlt,
            borderRadius: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          })}>
            <div style={{ fontSize: FontSizes.xxs, color: HR.inkSoft }}>
              平均每日工時（薪資計算用）
            </div>
            <div style={{ fontFamily: HR.mono, fontWeight: 700, fontSize: FontSizes.md }}>
              {avgHoursPerDay(f.work_schedule)} 小時
            </div>
          </div>
        </div>
      </Section>

      <Section title="其他" italic="Misc">
        <Field label="地址" span={2}>
          <TextInput value={f.address} onChange={set('address')} />
        </Field>
        <Field label="頭像 URL" span={2}>
          <TextInput value={f.avatar_url} onChange={set('avatar_url')} placeholder="https://…" mono />
        </Field>
        <Field label="備註" span={2}>
          <Textarea value={f.remark} onChange={set('remark')} rows={3} />
        </Field>
      </Section>
    </Modal>
  );
}

/* ─────────────────────────  Page  ─────────────────────────── */

function EmployeesPage() {
  const toast = useToast();
  const statuses = useEnum('employee_status');

  // Restore filter state from URL hash on mount: e.g. #/employees?q=alice&dept=2
  // This lets users bookmark / share filtered views, and lets the main page's
  // "online colleagues" deep-link with a name pre-filled.
  const [filters, setFilters] = React.useState(() => {
    const q = getHashQuery();
    return {
      page: parseInt(q.page, 10) || 1,
      page_size: parseInt(q.page_size, 10) || 20,
      department_id: q.dept ? parseInt(q.dept, 10) : null,
      status: q.status || null,
      include_deleted: q.deleted === '1',
      q: q.q || '',
    };
  });
  const [data, setData] = React.useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [loading, setLoading] = React.useState(true);
  const [departments, setDepartments] = React.useState([]);

  const [editTarget, setEditTarget] = React.useState(null);  // null | 'new' | emp obj
  const [delTarget, setDelTarget] = React.useState(null);
  const [delBusy, setDelBusy] = React.useState(false);
  const [showImport, setShowImport] = React.useState(false);

  // Sync filters → URL hash whenever they change.
  // setHashQuery uses replaceState so this doesn't fire hashchange / cause loops.
  React.useEffect(() => {
    setHashQuery({
      q: filters.q,
      dept: filters.department_id,
      status: filters.status,
      deleted: filters.include_deleted ? '1' : '',
      page: filters.page > 1 ? filters.page : '',
      page_size: filters.page_size !== 20 ? filters.page_size : '',
    });
  }, [filters]);

  // Load departments once for filter + form
  React.useEffect(() => {
    api('GET', '/departments?active_only=true')
      .then(rows => setDepartments(rows || []))
      .catch(e => toast(e.detail || '部門載入失敗', 'error'));
  }, []);

  // Load employees on filter change
  const refresh = React.useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    qs.set('page', filters.page);
    qs.set('page_size', filters.page_size);
    if (filters.department_id) qs.set('department_id', filters.department_id);
    if (filters.status) qs.set('status', filters.status);
    if (filters.include_deleted) qs.set('include_deleted', 'true');
    if (filters.q) qs.set('q', filters.q);
    api('GET', `/employees?${qs.toString()}`)
      .then(res => setData(res))
      .catch(e => toast(e.detail || '載入失敗', 'error'))
      .finally(() => setLoading(false));
  }, [filters, toast]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const doDelete = async () => {
    if (!delTarget) return;
    setDelBusy(true);
    try {
      await api('DELETE', `/employees/${delTarget.id}`);
      toast(`已刪除員工「${delTarget.name}」（可在「含已刪除」勾選後還原）`, 'ok');
      setDelTarget(null);
      refresh();
    } catch (e) {
      toast(e.detail || '刪除失敗', 'error');
    } finally { setDelBusy(false); }
  };

  const restore = async (emp) => {
    try {
      await api('POST', `/employees/${emp.id}/restore`);
      toast(`已還原「${emp.name}」`, 'ok');
      refresh();
    } catch (e) {
      toast(e.detail || '還原失敗', 'error');
    }
  };

  const resetFilters = () => setFilters({
    page: 1, page_size: 20,
    department_id: null, status: null, include_deleted: false, q: '',
  });

  return (
    <>
      <PageHeader
        eyebrow="人員管理 · Workforce"
        title="員工"
        italic="Employees"
        action={
          <div className="hr-emp-actions" style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="lg" icon="download" onClick={() => {
              window.open('/api/employees/template', '_blank');
            }}>
              下載範本
            </Button>
            <Button variant="ghost" size="lg" icon="upload" onClick={() => setShowImport(true)}>
              批次匯入
            </Button>
            <Button variant="primary" size="lg" icon="plus" onClick={() => setEditTarget('new')}>
              新增員工
            </Button>
          </div>
        }
      />

      {/* KPI strip — quick context above the filterable list */}
      <div className={GxCore.cls('rwd-grid-4', 'gx-p-emp-kpis')} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        <div style={EMP_gxTile({ padding: '14px 18px', background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6 })}>
          <div style={EMP_gxEyebrow({ fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.12em', textTransform: 'uppercase' })}>總員工數</div>
          <div style={EMP_gxKpiNum({ fontFamily: HR.serif, fontSize: FontSizes.xl, fontWeight: 500, color: HR.ink, marginTop: 4 })}>
            {data.total ?? '—'}
          </div>
        </div>
        <div style={EMP_gxTile({ padding: '14px 18px', background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6 })}>
          <div style={EMP_gxEyebrow({ fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.12em', textTransform: 'uppercase' })}>本頁顯示</div>
          <div style={EMP_gxKpiNum({ fontFamily: HR.serif, fontSize: FontSizes.xl, fontWeight: 500, color: HR.ink, marginTop: 4 })}>
            {data.items?.length ?? 0}
          </div>
        </div>
        <div style={EMP_gxTile({ padding: '14px 18px', background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6 })}>
          <div style={EMP_gxEyebrow({ fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.12em', textTransform: 'uppercase' })}>啟用部門</div>
          <div style={EMP_gxKpiNum({ fontFamily: HR.serif, fontSize: FontSizes.xl, fontWeight: 500, color: HR.ink, marginTop: 4 })}>
            {departments.filter(d => d.active).length}
          </div>
        </div>
        <div style={EMP_gxTile({ padding: '14px 18px', background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6 })}>
          <div style={EMP_gxEyebrow({ fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.12em', textTransform: 'uppercase' })}>未綁帳號</div>
          <div style={EMP_gxKpiNum({
            fontFamily: HR.serif, fontSize: FontSizes.xl, fontWeight: 500, marginTop: 4,
            color: data.items?.some(e => !e.user_id && !e.deleted_at) ? HR.warn : HR.ink,
          })}>
            {(data.items || []).filter(e => !e.user_id && !e.deleted_at).length}
          </div>
        </div>
      </div>

      <Card padding="0">
        <FilterBar
          filters={filters}
          setFilters={setFilters}
          departments={departments}
          statuses={statuses.options}
          onReset={resetFilters}
        />

        {loading ? (
          <div style={{ padding: 80, display: 'grid', placeItems: 'center' }}><Spinner size={20} /></div>
        ) : data.items.length === 0 ? (
          <EmptyState
            icon="file-text"
            title="無符合條件的員工"
            hint="試試調整篩選條件，或建立第一位員工。"
            action={<Button variant="primary" icon="plus" onClick={() => setEditTarget('new')}>新增員工</Button>}
          />
        ) : (
          <div className="va2-table-wrapper">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs, minWidth: 1100 }}>
              <thead>
                <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                  <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left', fontWeight: 500, whiteSpace: 'nowrap' }}>編號</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500, whiteSpace: 'nowrap' }}>員工</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500, whiteSpace: 'nowrap' }}>部門 / 職稱</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500, whiteSpace: 'nowrap' }}>狀態</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500, whiteSpace: 'nowrap' }}>主管</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500, whiteSpace: 'nowrap' }}>到職日</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500, whiteSpace: 'nowrap' }}>聯絡</th>
                  <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500, width: 200, whiteSpace: 'nowrap' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map(e => {
                  const isDeleted = !!e.deleted_at;
                  return (
                    <tr
                      key={e.id}
                      className="hr-row"
                      style={{
                        borderTop: `1px solid ${HR.ruleSoft}`,
                        opacity: isDeleted ? 0.55 : 1,
                      }}
                    >
                      <td style={{ padding: '14px 16px 14px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft, whiteSpace: 'nowrap' }}>
                        {e.emp_code || `#${e.id}`}
                      </td>
                      <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Avatar name={e.name} />
                          <div>
                            <div style={{
                              fontWeight: 500,
                              textDecoration: isDeleted ? 'line-through' : 'none',
                            }}>{e.name}</div>
                            {(e.name_en || e.username) && (
                              <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontStyle: e.name_en ? uiPick('italic', 'normal') : 'normal' }}>
                                {e.name_en || (e.username && `@${e.username}`)}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                        <div>{e.department_name || <span style={{ color: HR.inkFaint }}>—</span>}</div>
                        {e.position && <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>{e.position}</div>}
                      </td>
                      <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                        {isDeleted
                          ? <Pill tone="down" dot>已刪除</Pill>
                          : <StatusPill code={e.status} enums={statuses} />}
                      </td>
                      <td style={{ padding: '14px 16px', color: HR.inkSoft, whiteSpace: 'nowrap' }}>
                        {e.manager_name || <span style={{ color: HR.inkFaint }}>—</span>}
                      </td>
                      <td style={{ padding: '14px 16px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft, whiteSpace: 'nowrap' }}>
                        {fmtDate(e.hire_date)}
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: FontSizes.xxs, color: HR.inkSoft }}>
                        {e.email && <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180, whiteSpace: 'nowrap' }} title={e.email}>{e.email}</div>}
                        {e.phone && <div style={{ fontFamily: HR.mono }}>{e.phone}</div>}
                        {!e.email && !e.phone && <span style={{ color: HR.inkFaint }}>—</span>}
                      </td>
                      <td style={{ padding: '14px 24px 14px 16px', textAlign: 'right' }}>
                        <span className="hr-row-actions" style={{ display: 'inline-flex', gap: 6 }}>
                          {isDeleted ? (
                            <Button size="sm" variant="ghost" onClick={() => restore(e)}>還原</Button>
                          ) : (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => setEditTarget(e)}>編輯</Button>
                              <Button size="sm" variant="ghost" onClick={() => setDelTarget(e)} style={{ color: HR.down, borderColor: GxCore.alpha(HR.down, '55') }}>刪除</Button>
                            </>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={data.page || filters.page}
          pageSize={data.page_size || filters.page_size}
          total={data.total || 0}
          onPageChange={(p) => setFilters(f => ({ ...f, page: p }))}
          onPageSizeChange={(n) => setFilters(f => ({ ...f, page: 1, page_size: n }))}
        />
        <style>{`
          .hr-row:hover { background: ${HR.surfaceAlt}; }
          /* Actions always visible — no hover-reveal */
        `}</style>
      </Card>

      {editTarget && (
        <EmployeeForm
          initial={editTarget === 'new' ? null : editTarget}
          departments={departments}
          onCancel={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); refresh(); }}
        />
      )}

      <ConfirmDialog
        open={!!delTarget}
        title={delTarget ? `刪除員工「${delTarget.name}」？` : ''}
        message={
          <>
            適用於「新增錯誤想移除」或「員工已離職」兩種情境。
            <br /><br />
            此為<strong>軟刪除</strong> — 員工資料會保留在資料庫，可隨時還原。員工會從預設清單中隱藏，需勾選「<strong>含已刪除</strong>」才能看到，並可按「還原」復原。
          </>
        }
        danger
        busy={delBusy}
        onCancel={() => setDelTarget(null)}
        onConfirm={doDelete}
      />
      {showImport && (
        <ExcelImportModal
          onClose={() => setShowImport(false)}
          onSaved={() => { setShowImport(false); refresh(); }}
        />
      )}
    </>
  );
}

/* ─────────────────────────  Excel 批次匯入 modal  ───────────── */
/* Upload .xlsx → POST /api/employees/import → 顯示 success / skip / errors 摘要。
 * 拖拉區用 input[file] 全覆蓋技巧 + drag class 切換。 */

function ExcelImportModal({ onClose, onSaved }) {
  const toast = useToast();
  const [file, setFile] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState('');

  const onPick = (f) => {
    if (!f) return;
    if (!/\.xlsx?$/i.test(f.name)) {
      setErr('請選擇 .xlsx 格式檔案'); return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setErr('檔案大小超過 10MB'); return;
    }
    setErr(''); setFile(f); setResult(null);
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/employees/import', {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `匯入失敗 (${res.status})`);
      }
      const data = await res.json();
      setResult(data);
      toast(`匯入完成：新增 ${data.success}、略過 ${data.skipped}、錯誤 ${data.errors.length}`,
        data.errors.length > 0 ? 'warn' : 'ok');
      if (data.success > 0) onSaved();
    } catch (e) {
      setErr(e.message || '匯入失敗');
    } finally { setBusy(false); }
  };

  const reset = () => { setFile(null); setResult(null); setErr(''); };

  return (
    <Modal
      open
      onClose={busy ? null : onClose}
      title="批次匯入員工"
      subtitle="Bulk Import Employees"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>關閉</Button>
          {file && !result && (
            <Button variant="primary" onClick={submit} disabled={busy}>
              {busy ? '匯入中…' : '開始匯入'}
            </Button>
          )}
          {result && (
            <Button variant="primary" onClick={reset}>再匯入一批</Button>
          )}
        </>
      }
    >
      {/* Step indicators */}
      <div style={{
        display: 'flex', gap: 8, alignItems: 'center', fontSize: FontSizes.trading_xl,
        marginBottom: 16, color: HR.inkSoft,
      }}>
        <span style={{ color: HR.accent, fontWeight: 600 }}>① 下載範本</span>
        <span>→</span>
        <span style={{ color: file ? HR.accent : HR.ink, fontWeight: 600 }}>② 填寫資料</span>
        <span>→</span>
        <span style={{ color: result ? HR.accent : HR.inkFaint, fontWeight: 600 }}>③ 上傳匯入</span>
      </div>

      {/* Template download */}
      <a
        href="/api/employees/template"
        style={uiStyle({
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 16px',
          background: HR.surfaceAlt,
          border: `1px solid ${HR.rule}`,
          borderRadius: 4,
          textDecoration: 'none',
          color: HR.ink,
          marginBottom: 14,
        }, { borderRadius: GxT.radius.inner, border: 'none', background: GxT.color.s2 })}
      >
        <Icon name="download" size={16} stroke={1.8} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: FontSizes.xs, fontWeight: 500 }}>員工匯入範本.xlsx</div>
          <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 2 }}>
            點擊下載含中文標題的官方範本
          </div>
        </div>
        <span style={{ fontSize: FontSizes.trading_lg, color: HR.accent, fontFamily: HR.mono }}>下載</span>
      </a>

      {/* Drop zone */}
      {!result && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault(); setDragOver(false);
            onPick(e.dataTransfer.files?.[0]);
          }}
          style={{
            position: 'relative',
            border: `2px dashed ${dragOver ? HR.accent : (file ? HR.accent : HR.rule)}`,
            borderRadius: uiPick(6, GxT.radius.card),
            padding: '28px 20px',
            textAlign: 'center',
            background: dragOver ? HR.accentSoft : (file ? HR.accentSoft : HR.surfaceAlt),
            transition: 'border-color 120ms, background 120ms',
            cursor: 'pointer',
          }}
        >
          {file ? (
            <>
              <Icon name="file-text" size={32} stroke={1.5} />
              <div style={{ marginTop: 8, fontWeight: 500, fontSize: FontSizes.xs, color: HR.ink }}>
                {file.name}
              </div>
              <div style={{ marginTop: 4, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
                {(file.size / 1024).toFixed(1)} KB · 點擊「開始匯入」上傳
              </div>
            </>
          ) : (
            <>
              <Icon name="upload" size={32} stroke={1.5} />
              <div style={{ marginTop: 8, fontWeight: 500, fontSize: FontSizes.xs, color: HR.ink }}>
                拖拉 Excel 至此
              </div>
              <div style={{ marginTop: 4, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
                或點擊選擇檔案（.xlsx，最大 10MB）
              </div>
            </>
          )}
          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => onPick(e.target.files?.[0])}
            style={{
              position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer',
              width: '100%', height: '100%',
            }}
          />
        </div>
      )}

      {/* Error */}
      {err && (
        <div style={{
          marginTop: 10, padding: '8px 12px',
          background: '#FCE4E4', color: HR.down,
          borderRadius: uiPick(4, GxT.radius.inner), fontSize: FontSizes.xxs,
        }}>
          ⚠ {err}
        </div>
      )}

      {/* Result */}
      {result && (
        <div style={{ marginTop: 12 }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10,
            marginBottom: 14,
          }}>
            <div style={{ padding: 12, background: HR.accentSoft, borderRadius: uiPick(4, GxT.radius.inner), textAlign: 'center' }}>
              <div style={EMP_gxEyebrow({ fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.1em', textTransform: 'uppercase' })}>新增</div>
              <div style={{ fontFamily: HR.mono, fontSize: FontSizes.lg, fontWeight: 600, color: HR.accent, marginTop: 4 }}>
                {result.success}
              </div>
            </div>
            <div style={{ padding: 12, background: HR.surfaceAlt, borderRadius: uiPick(4, GxT.radius.inner), textAlign: 'center' }}>
              <div style={EMP_gxEyebrow({ fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.1em', textTransform: 'uppercase' })}>略過</div>
              <div style={{ fontFamily: HR.mono, fontSize: FontSizes.lg, fontWeight: 600, color: HR.inkSoft, marginTop: 4 }}>
                {result.skipped}
              </div>
            </div>
            <div style={{
              padding: 12, background: result.errors.length ? '#FCE4E4' : HR.surfaceAlt,
              borderRadius: uiPick(4, GxT.radius.inner), textAlign: 'center',
            }}>
              <div style={EMP_gxEyebrow({ fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.1em', textTransform: 'uppercase' })}>錯誤</div>
              <div style={{
                fontFamily: HR.mono, fontSize: FontSizes.lg, fontWeight: 600, marginTop: 4,
                color: result.errors.length ? HR.down : HR.inkSoft,
              }}>
                {result.errors.length}
              </div>
            </div>
          </div>
          {result.errors.length > 0 && (
            <div style={{
              padding: 12, background: '#FFF5F5',
              border: `1px solid #FCC`, borderRadius: uiPick(4, GxT.radius.inner),
              maxHeight: 200, overflowY: 'auto',
              fontSize: FontSizes.xxs, color: HR.down,
            }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>錯誤明細：</div>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {result.errors.map((msg, i) => (
                  <li key={i} style={{ marginBottom: 2 }}>{msg}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ─────────────────────────  Dashboard (整合主站的 landing)  ─────── */
/* When user clicks "ERP" from main page sidebar, this is the first thing
 * they see. Goal: greet, surface "what should I do now", give quick
 * routes to the rest of the console.
 *
 * Reads:
 *   /auth/me                    — greeting + role
 *   /employees/me               — try to grab self employee record (404 OK)
 *   /employees?page_size=1      — total headcount
 *   /departments?active_only=false
 *   /attendance/punches?...     — today's punch for self
 *   /attendance/daily?...       — month-to-date hours for self
 *   window.FIX_LEAVE_REQUESTS / FIX_CLAIMS — mock counts (banner-flagged)
 */

function greetingFor(name) {
  const hour = new Date().getHours();
  const part = hour < 5 ? '深夜好' :
    hour < 11 ? 'Good morning' :
      hour < 14 ? '午安' :
        hour < 18 ? 'Good afternoon' :
          'Good evening';
  const display = name || 'there';
  return `${part}, ${display}.`;
}

function ActionRow({ icon = 'arrow-right', tone = 'neutral', title, hint, count, onClick, mock }) {
  const tones = {
    neutral: HR.inkSoft, accent: HR.accent, warn: HR.warn, down: HR.down,
  };
  const c = tones[tone] || HR.inkSoft;
  return (
    <div
      onClick={onClick}
      style={{
        display: 'grid', gridTemplateColumns: '32px 1fr auto auto', gap: 12,
        padding: '12px 14px', borderRadius: 5, cursor: 'pointer',
        alignItems: 'center', borderBottom: `1px solid ${HR.ruleSoft}`,
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = HR.surfaceAlt}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      <span style={{
        width: 28, height: 28, borderRadius: '50%',
        background: GxCore.alpha(c, '15'), color: c,
        display: 'grid', placeItems: 'center',
      }}>
        <Icon name={icon} size={13} stroke={2} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: FontSizes.trading_md, fontWeight: 500, color: HR.ink, display: 'flex', alignItems: 'center', gap: 8 }}>
          {title}
          {mock && <span style={{
            fontSize: FontSizes.trading_xxs, padding: '1px 5px', borderRadius: 3,
            background: HR.warn, color: '#fff', fontWeight: 600,
            letterSpacing: '0.05em', fontFamily: HR.mono,
          }}>MOCK</span>}
        </div>
        {hint && <div style={{ fontSize: FontSizes.trading_xl, color: HR.inkFaint, marginTop: 2 }}>{hint}</div>}
      </div>
      {count != null && (
        <div style={{
          fontFamily: HR.mono, fontSize: FontSizes.lg, fontWeight: 600,
          color: count > 0 ? c : HR.inkFaint, fontVariantNumeric: 'tabular-nums',
        }}>{count}</div>
      )}
      <Icon name="arrow-right" size={13} className="hr-action-arrow" />
    </div>
  );
}

function ModuleTile({ name, icon, route, mock, accent }) {
  return (
    <a
      href={'#/' + route}
      style={{
        display: 'flex', flexDirection: 'column', gap: 8,
        padding: '16px 14px', borderRadius: 6,
        background: HR.surface, border: `1px solid ${HR.rule}`,
        textDecoration: 'none', color: HR.ink, position: 'relative',
        transition: 'all 120ms', cursor: 'pointer',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = HR.surfaceAlt; e.currentTarget.style.borderColor = HR.accent; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = HR.surface; e.currentTarget.style.borderColor = HR.rule; }}
    >
      <span style={{
        width: 28, height: 28, borderRadius: 5,
        background: accent ? HR.accentSoft : HR.ruleSoft,
        color: accent ? HR.accent : HR.inkSoft,
        display: 'grid', placeItems: 'center',
      }}>
        <Icon name={icon} size={14} />
      </span>
      <span style={{ fontSize: FontSizes.xs, fontWeight: 500 }}>{name}</span>
      {mock && (
        <span style={{
          position: 'absolute', top: 10, right: 10,
          fontSize: FontSizes.trading_3xs, padding: '1px 4px', borderRadius: 2,
          background: HR.warn, color: '#fff', fontWeight: 600,
          letterSpacing: '0.05em', fontFamily: HR.mono,
        }}>MOCK</span>
      )}
    </a>
  );
}

function DashboardPage() {
  const toast = useToast();
  const [user, setUser] = React.useState(null);
  const [employees, setEmployees] = React.useState({ total: 0, items: [] });
  const [departments, setDepartments] = React.useState([]);
  const [myEmp, setMyEmp] = React.useState(null);
  const [todayPunch, setTodayPunch] = React.useState(null);
  const [monthMins, setMonthMins] = React.useState(0);
  const [unboundCount, setUnboundCount] = React.useState(null);

  React.useEffect(() => {
    api('GET', '/auth/me').then(setUser).catch(() => { });
  }, []);

  React.useEffect(() => {
    if (!user) return;
    const isHR = isHrOrAdmin(user);
    const today = todayStr();
    const monthFrom = today.slice(0, 8) + '01';

    Promise.all([
      api('GET', '/employees?page=1&page_size=1').catch(() => ({ total: 0, items: [] })),
      api('GET', '/departments?active_only=false').catch(() => []),
      api('GET', '/employees/me').catch(() => null),
      api('GET', `/attendance/punches?from_date=${today}&to_date=${today}&page_size=5`).catch(() => ({ items: [] })),
      api('GET', `/attendance/daily?from_date=${monthFrom}&to_date=${today}&page_size=50`).catch(() => ({ items: [] })),
    ]).then(([empsP, depts, me, punches, daily]) => {
      setEmployees(empsP);
      setDepartments(depts || []);
      setMyEmp(me);
      setTodayPunch((punches.items && punches.items[0]) || null);
      setMonthMins((daily.items || []).reduce((s, d) => s + (d.work_minutes || 0), 0));
    });

    // HR-only: count employees without user binding (data-quality alert)
    if (isHR) {
      api('GET', '/employees?page=1&page_size=200').then(res => {
        const n = (res.items || []).filter(e => !e.user_id && !e.deleted_at).length;
        setUnboundCount(n);
      }).catch(() => setUnboundCount(null));
    }
  }, [user]);

  if (!user) {
    return <div style={{ padding: 80, display: 'grid', placeItems: 'center' }}><Spinner /></div>;
  }

  const isHR = isHrOrAdmin(user);
  const greetingName = user.display_name || user.username || '';
  const fixLeave = window.FIX_LEAVE_REQUESTS || [];
  const fixClaims = window.FIX_CLAIMS || [];
  const pendingLeaveCount = fixLeave.filter(r => r.status === 'pending').length;
  const pendingClaimCount = fixClaims.filter(c => c.status === 'pending').length;
  const pendingClaimAmount = fixClaims.filter(c => c.status === 'pending').reduce((s, c) => s + c.total, 0);
  const myPendingClaim = fixClaims.filter(c => c.employee_id === 5 && c.status === 'pending');
  const myUpcomingLeave = fixLeave.filter(r => r.employee_id === 5 && r.status === 'pending');

  return (
    <>
      {/* ── Greeting (mirrors main page Trading Desk masthead) ── */}
      <div style={uiStyle({
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        paddingBottom: 22, borderBottom: `2px solid ${HR.ink}`, marginBottom: 28,
      }, { padding: '4px 4px 0', borderBottom: 'none', flexWrap: 'wrap', gap: 16 })}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
            <div style={uiStyle(
              { fontSize: FontSizes.trading_lg, letterSpacing: '0.24em', textTransform: 'uppercase', color: HR.inkFaint },
              { fontSize: GxT.size.label, letterSpacing: 0, textTransform: 'none', color: GxT.color.p, fontWeight: GxT.weight.label },
            )}>
              人員管理 · Overview
            </div>
            <div style={{ width: 24, height: 1, background: HR.inkFaint }} />
            <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontFamily: HR.mono }}>
              {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
            </div>
          </div>
          <h1 style={uiStyle({
            fontFamily: HR.serif, fontSize: FontSizes.xl, fontWeight: 400,
            letterSpacing: '-0.02em', lineHeight: 1, margin: 0, color: HR.ink,
          }, { fontSize: 'clamp(28px, 6vw, 40px)', fontWeight: GxT.weight.display, letterSpacing: '-0.5px', lineHeight: 1.12 })}>
            {greetingFor(greetingName)}
          </h1>
          <div style={{ fontSize: FontSizes.xs, color: HR.inkSoft, marginTop: 10 }}>
            {isHR
              ? `你的角色：${(user.roles || []).join(' · ') || user.role || '—'} · 全公司資料管理權限`
              : '你的視野：個人出勤 / 請假 / 報帳。需要管理權限請聯絡 HR。'}
          </div>
        </div>
        <Button variant="primary" size="lg" icon="arrow-right" onClick={() => navigate(isHR ? '/employees' : '/attendance')}>
          {isHR ? '前往員工管理' : '看我的出勤'}
        </Button>
      </div>

      {/* ── Lede stats ── */}
      <div className="hr-emp-lede-stats" style={{
        display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr',
        borderBottom: `1px solid ${HR.rule}`, paddingBottom: 24, marginBottom: 28,
      }}>
        <div style={{ paddingRight: 32, borderRight: `1px solid ${HR.rule}` }}>
          <div style={EMP_gxEyebrow({ fontSize: FontSizes.trading_md, letterSpacing: '0.16em', textTransform: 'uppercase', color: HR.inkFaint, marginBottom: 12 })}>
            {isHR ? '在職員工 · Active Headcount' : '本月工時 · Month-to-date'}
          </div>
          {isHR ? (
            <>
              <div style={EMP_gxKpiNum({
                fontFamily: HR.serif, fontSize: FontSizes.xl, fontWeight: 400,
                letterSpacing: '-0.03em', lineHeight: 0.95, color: HR.ink,
                fontVariantNumeric: 'tabular-nums',
              })}>{employees.total}</div>
              <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, marginTop: 10 }}>
                橫跨 {departments.filter(d => d.active).length} 個啟用部門 · {departments.length} 個總計
              </div>
            </>
          ) : (
            <>
              <div style={EMP_gxKpiNum({
                fontFamily: HR.serif, fontSize: FontSizes.xl, fontWeight: 400,
                letterSpacing: '-0.03em', lineHeight: 0.95, color: HR.ink,
                fontVariantNumeric: 'tabular-nums',
              })}>
                {Math.floor(monthMins / 60)}<span style={{ fontSize: FontSizes.xl, color: HR.inkFaint }}>h</span>
              </div>
              <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, marginTop: 10 }}>
                {myEmp?.department_name || '—'} · {myEmp?.position || '—'}
              </div>
            </>
          )}
        </div>

        {/* Card 2: 部門 (HR) / 今日打卡 (員工) */}
        <div style={{ padding: '0 24px', borderRight: `1px solid ${HR.rule}` }}>
          <div style={EMP_gxEyebrow({ fontSize: FontSizes.trading_md, letterSpacing: '0.16em', textTransform: 'uppercase', color: HR.inkFaint, marginBottom: 12 })}>
            {isHR ? '部門' : '今日打卡'}
          </div>
          {isHR ? (
            <>
              <div style={EMP_gxKpiNum({ fontFamily: HR.serif, fontSize: FontSizes.xl, lineHeight: 1, color: HR.ink })}>{departments.length}</div>
              <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, marginTop: 8 }}>
                {departments.filter(d => d.active).length} 啟用 · {departments.filter(d => !d.active).length} 停用
              </div>
            </>
          ) : todayPunch ? (
            <>
              <div style={EMP_gxKpiNum({ fontFamily: HR.serif, fontSize: FontSizes.xl, lineHeight: 1, color: HR.up })}>
                {fmtTime(todayPunch.punch_time)}
              </div>
              <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 8, fontFamily: HR.mono }}>
                {fmtTime(todayPunch.punch_time)} – {fmtTime(todayPunch.abort_time)} · {fmtDuration(todayPunch.work_minutes)}
              </div>
            </>
          ) : (
            <>
              <div style={EMP_gxKpiNum({ fontFamily: HR.serif, fontSize: FontSizes.lg, lineHeight: 1, color: HR.inkFaint })}>—</div>
              <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 8 }}>今日尚無打卡</div>
            </>
          )}
        </div>

        {/* Card 3: 待我審核 (HR) / 我的請假 (員工) */}
        <div style={{ padding: '0 24px', borderRight: `1px solid ${HR.rule}` }}>
          <div style={EMP_gxEyebrow({ fontSize: FontSizes.trading_md, letterSpacing: '0.16em', textTransform: 'uppercase', color: HR.inkFaint, marginBottom: 12 })}>
            {isHR ? '待我審核' : '我的請假'}
          </div>
          <div style={EMP_gxKpiNum({
            fontFamily: HR.serif, fontSize: FontSizes.xl, lineHeight: 1,
            color: ((isHR ? pendingLeaveCount + pendingClaimCount : myUpcomingLeave.length) > 0) ? HR.warn : HR.inkFaint,
          })}>
            {isHR ? (pendingLeaveCount + pendingClaimCount) : myUpcomingLeave.length}
          </div>
          <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 8 }}>
            {isHR
              ? `${pendingLeaveCount} 請假 · ${pendingClaimCount} 報帳`
              : `件審核中 · 餘額查 #/leave`}
          </div>
        </div>

        {/* Card 4: 資料品質 (HR) / 報帳 (員工) */}
        <div style={{ padding: '0 24px' }}>
          <div style={EMP_gxEyebrow({ fontSize: FontSizes.trading_md, letterSpacing: '0.16em', textTransform: 'uppercase', color: HR.inkFaint, marginBottom: 12 })}>
            {isHR ? '未綁帳號員工' : '我的報帳'}
          </div>
          {isHR ? (
            <>
              <div style={EMP_gxKpiNum({
                fontFamily: HR.serif, fontSize: FontSizes.xl, lineHeight: 1,
                color: (unboundCount ?? 0) > 0 ? HR.warn : HR.up,
              })}>{unboundCount ?? '—'}</div>
              <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 8 }}>
                資料品質 · 建議建 user_id
              </div>
            </>
          ) : (
            <>
              <div style={EMP_gxKpiNum({
                fontFamily: HR.serif, fontSize: FontSizes.xl, lineHeight: 1,
                color: myPendingClaim.length > 0 ? HR.warn : HR.inkFaint,
              })}>{myPendingClaim.length}</div>
              <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 8 }}>
                {myPendingClaim.length > 0
                  ? `共 ${fmtMoney(myPendingClaim.reduce((s, c) => s + c.total, 0))}`
                  : '無進行中報帳'}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Action panels: TODO list + Module quick-launch ── */}
      <div className="hr-emp-dashboard-panels" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 24 }}>
        <Card eyebrow="To-do" title="待處理任務" italic={isHR ? "Approvals & Data" : "Your Day"} padding="0">
          {isHR ? (
            <div>
              {pendingLeaveCount > 0 && (
                <ActionRow
                  icon="calendar" tone="warn" mock
                  title="請假申請待審核"
                  hint={`${pendingLeaveCount} 筆等候簽核 · 點擊進入審核佇列`}
                  count={pendingLeaveCount}
                  onClick={() => navigate('/leave')}
                />
              )}
              {pendingClaimCount > 0 && (
                <ActionRow
                  icon="dollar-sign" tone="warn" mock
                  title="報帳單待審核"
                  hint={`${pendingClaimCount} 筆 · 共 ${fmtMoney(pendingClaimAmount)}`}
                  count={pendingClaimCount}
                  onClick={() => navigate('/expense')}
                />
              )}
              {(unboundCount ?? 0) > 0 && (
                <ActionRow
                  icon="file-text" tone="accent"
                  title="員工未綁定系統帳號"
                  hint="建議補建 user_id 以利打卡同步與本人查詢"
                  count={unboundCount}
                  onClick={() => navigate('/employees')}
                />
              )}
              {pendingLeaveCount === 0 && pendingClaimCount === 0 && (unboundCount ?? 0) === 0 && (
                <EmptyState
                  icon="search"
                  title="目前沒有待你處理的任務"
                  hint="所有審核都已清空 — 是該休息一下了 ☕"
                />
              )}
            </div>
          ) : (
            <div>
              {!todayPunch && (
                <ActionRow
                  icon="clock" tone="warn"
                  title="今日尚無打卡紀錄"
                  hint="HR 會匯入打卡機資料；如有疑問請聯繫人資"
                  onClick={() => navigate('/attendance')}
                />
              )}
              {myUpcomingLeave.length > 0 && (
                <ActionRow
                  icon="calendar" tone="accent" mock
                  title={`你有 ${myUpcomingLeave.length} 筆請假審核中`}
                  hint={myUpcomingLeave.map(r => `${leaveTypeLabel(r.leave_type)} ${r.from_date}`).join(' · ')}
                  count={myUpcomingLeave.length}
                  onClick={() => navigate('/leave')}
                />
              )}
              {myPendingClaim.length > 0 && (
                <ActionRow
                  icon="dollar-sign" tone="accent" mock
                  title={`你有 ${myPendingClaim.length} 筆報帳審核中`}
                  hint={`共 ${fmtMoney(myPendingClaim.reduce((s, c) => s + c.total, 0))}`}
                  count={myPendingClaim.length}
                  onClick={() => navigate('/expense')}
                />
              )}
              {!myEmp && (
                <ActionRow
                  icon="file-text" tone="warn"
                  title="你的帳號還沒綁定員工資料"
                  hint="出勤/請假/報帳功能會受限；請聯絡 HR 建立員工記錄"
                  onClick={() => { }}
                />
              )}
              {todayPunch && myUpcomingLeave.length === 0 && myPendingClaim.length === 0 && myEmp && (
                <EmptyState
                  icon="search"
                  title="今天沒有需要你處理的事"
                  hint="專心工作吧 💪"
                />
              )}
            </div>
          )}
        </Card>

        <Card eyebrow="Modules" title="模組快捷" italic="Quick Launch" padding="14px">
          <div className="hr-emp-module-tiles" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            <ModuleTile name="員工" icon="file-text" route="employees" accent />
            <ModuleTile name="部門" icon="folder" route="departments" accent />
            <ModuleTile name="出勤" icon="clock" route="attendance" accent />
            <ModuleTile name="請假" icon="calendar" route="leave" mock />
            <ModuleTile name="報帳" icon="dollar-sign" route="expense" mock />
            <ModuleTile name="薪資" icon="dollar-sign" route="payroll" mock />
            <ModuleTile name="發票・GL" icon="file-text" route="invoices" mock />
            <ModuleTile name="行事曆" icon="calendar" route="calendar" mock />
            <ModuleTile name="設定" icon="settings" route="settings" mock />
          </div>
          <div style={{ marginTop: 14, padding: '10px 12px', background: HR.surfaceAlt, borderRadius: 4, fontSize: FontSizes.trading_xl, color: HR.inkFaint, lineHeight: 1.5 }}>
            <strong style={{ color: HR.inkSoft, fontWeight: 500 }}>MOCK</strong> 標籤的模組為 fixture 預覽，等待後端串接。
          </div>
        </Card>
      </div>
    </>
  );
}

window.EmployeesPage = EmployeesPage;
window.DashboardPage = DashboardPage;
